import { useCallback, useRef } from 'react';

import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { loadKount } from 'utils/fraud-prevention';

declare global {
  interface CountSDK {
    sessionId: string;
    ClientSDK: new () => {
      autoLoadEvents: () => void;
    };
  }

  interface Window {
    ka?: CountSDK;
  }
}

export const useKountSessionId = () => {
  const kountSessionId = useRef('');
  const { enqueueIfNotDrained, drainQueue } = useReadyQueue();
  const kountCb = enqueueIfNotDrained(() => {
    if (window.ka) {
      const client = new window.ka.ClientSDK();
      client.autoLoadEvents();
    }
  });

  /**
   * Sets the kount session id to a random uuid.
   * Sends the new session to Kount.
   */
  const resetKountSessionId = useCallback(() => {
    const callback = (sessionId: string) => {
      kountSessionId.current = sessionId;
      drainQueue();
    };

    kountCb();
    loadKount(callback);
  }, [drainQueue, kountCb]);

  /**
   * Set the kountSessionId on load
   */
  useEffectOnce(() => {
    // NOTE: cypress-v2 test suite requirement
    // do not load KOUNT when running in Cypress
    // kount validation skipped for cypress test users in the BE
    if (window.Cypress) {
      return;
    }

    resetKountSessionId();
  });

  return {
    kountSessionId: kountSessionId.current,
    resetKountSessionId
  };
};
